import { Button, Col, Row } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { Loader } from "../../Common/Components";
import Page from "../../Common/Page";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputs } from "../../components/Form/StyledFormInputs";
import { useCreateConference, useFetchConference, useUpdateConference } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import type { IConferense } from "./types";

const PermissionName = PermissionNames.CONFERENCE;

type IFormInput = Pick<IConferense, "abbreviationLatin" | "abbreviationLocal" | "nameLatin" | "nameLocal">;
const ManageConferences = () => {
  const { sport, organizationId, customerId } = useGetOrgData();
  const { dataId } = useParams();

  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const { data, isLoading: isFetchLoading } = useFetchConference();
  const { mutate: createFn, isPending: isCreatePending } = useCreateConference();
  const { mutate: updateFn, isPending: isUpdatePending } = useUpdateConference();

  useEffect(() => {
    reset({
      nameLocal: data?.nameLocal,
      nameLatin: data?.nameLatin,
      abbreviationLocal: data?.abbreviationLocal,
      abbreviationLatin: data?.abbreviationLatin,
    });
  }, [data]);

  const createConference = useCallback(
    (data: IFormInput) => {
      // @ts-ignore
      createFn(data);
    },
    [createFn],
  );

  const updateConference = useCallback(
    (data: IFormInput) => {
      // @ts-ignore
      updateFn(data);
    },
    [updateFn],
  );

  const title = useMemo(() => {
    return dataId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data?.nameLocal ? `- ${data?.nameLocal}` : ""}`
      : `${formatMessage({
          id: "conference.add",
          defaultMessage: "Adding a New Conference",
        })}`;
  }, [dataId, formatMessage, data]);

  const onSubmit: SubmitHandler<IFormInput> = useCallback(
    (data) => {
      if (dataId) {
        updateConference(data);
      } else {
        createConference(data);
      }
    },
    [dataId, updateConference, createConference],
  );
  return (
    <Page title={title} tabTitle={title}>
      {isUpdatePending || isCreatePending || isFetchLoading ? (
        <Loader />
      ) : (
        <Content>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local"
                      name="nameLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "conference",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "name.local",
                            }),
                          },
                        ),
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLocal"
                      labelTooltip={formatMessage({
                        id: "form.help.local.abbreviation",
                      })}
                      name="abbreviationLocal"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin"
                      name="nameLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "conference",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLatin"
                      labelTooltip={formatMessage({
                        id: "form.help.latin.abbreviation",
                      })}
                      name="abbreviationLatin"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginTop: 20 }}>
              <PermissionCheck
                permissionKeys={[PermissionName]}
                permissionOptions={dataId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
              >
                <Button htmlType="submit" type="primary">
                  {dataId ? formatMessage({ id: "conference.update" }) : formatMessage({ id: "conference.add" })}
                </Button>
              </PermissionCheck>
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences`}>
                <Button style={{ marginLeft: 30 }} type="primary">
                  {formatMessage({ id: "close" })}
                </Button>
              </Link>
            </Row>
          </form>
        </Content>
      )}
    </Page>
  );
};

const Content = styled.div`
display: flex;

  .container {
    margin: 0;
  }
`;

export default ManageConferences;
